import React, { useMemo } from 'react';
import type { ApexOptions } from 'apexcharts';
import { Typography } from 'antd';
import Chart from 'react-apexcharts';

import { AssetEodPreview } from 'state';

type PreviewPoint = Pick<
  AssetEodPreview['data'][number],
  'timestamp' | 'bid' | 'ask' | 'bid_yield' | 'ask_yield'
> & { close?: number | null };
type ChartPoint = { x: string; y: number | null };
type ChartSeries = {
  name: string;
  type: 'line';
  data: ChartPoint[];
};

type Props = {
  data: PreviewPoint[];
  intraday?: boolean;
};

const PRICE_SERIES_NAMES = ['bid', 'close', 'ask'];
const YIELD_SERIES_NAMES = ['bid yield', 'ask yield'];

const finiteOrNull = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const formatPrice = (value: number) =>
  value.toLocaleString('en', {
    maximumFractionDigits: 4,
    minimumFractionDigits: 0,
  });

const formatYield = (value: number) =>
  `${value.toLocaleString('en', {
    maximumFractionDigits: 3,
    minimumFractionDigits: 0,
  })}%`;

export const buildAfsEodSeries = (data: PreviewPoint[]): ChartSeries[] => {
  const bid: ChartPoint[] = [];
  const close: ChartPoint[] = [];
  const ask: ChartPoint[] = [];
  const bidYield: ChartPoint[] = [];
  const askYield: ChartPoint[] = [];
  let hasYieldData = false;

  data.forEach(point => {
    const storedBidYield = finiteOrNull(point.bid_yield);
    const storedAskYield = finiteOrNull(point.ask_yield);
    if (storedBidYield !== null || storedAskYield !== null) {
      hasYieldData = true;
    }

    bid.push({ x: point.timestamp, y: finiteOrNull(point.bid) });
    close.push({ x: point.timestamp, y: finiteOrNull(point.close) });
    ask.push({ x: point.timestamp, y: finiteOrNull(point.ask) });
    bidYield.push({ x: point.timestamp, y: storedBidYield });
    askYield.push({ x: point.timestamp, y: storedAskYield });
  });

  const priceSeries: ChartSeries[] = [
    { name: PRICE_SERIES_NAMES[0], type: 'line', data: bid },
    { name: PRICE_SERIES_NAMES[1], type: 'line', data: close },
    { name: PRICE_SERIES_NAMES[2], type: 'line', data: ask },
  ];

  if (!hasYieldData) return priceSeries;

  return [
    ...priceSeries,
    { name: YIELD_SERIES_NAMES[0], type: 'line', data: bidYield },
    { name: YIELD_SERIES_NAMES[1], type: 'line', data: askYield },
  ];
};

export const buildAfsEodOptions = (
  hasYieldData: boolean,
  intraday = false,
): ApexOptions => ({
  chart: {
    animations: { enabled: false },
    toolbar: { show: false },
  },
  colors: ['#008FFB', '#00E396', '#FEB019', '#775DD0', '#E91E63'],
  dataLabels: { enabled: false },
  markers: { size: 0 },
  stroke: {
    curve: 'straight',
    width: hasYieldData ? [3, 3, 3, 4, 4] : [3, 3, 3],
    dashArray: hasYieldData ? [0, 0, 0, 0, 0] : [0, 0, 0],
  },
  tooltip: {
    shared: true,
    hideEmptySeries: true,
    x: { format: intraday ? 'dd MMM yyyy HH:mm:ss' : 'dd MMM yyyy' },
    y: [
      { formatter: formatPrice },
      { formatter: formatPrice },
      { formatter: formatPrice },
      ...(hasYieldData
        ? [{ formatter: formatYield }, { formatter: formatYield }]
        : []),
    ],
  },
  xaxis: {
    type: 'datetime',
    tickPlacement: 'between',
    tooltip: { enabled: false },
  },
  yaxis: hasYieldData
    ? [
        {
          forceNiceScale: true,
          seriesName: PRICE_SERIES_NAMES,
          title: { text: 'Price' },
          labels: { formatter: formatPrice },
        },
        {
          decimalsInFloat: 3,
          forceNiceScale: true,
          opposite: true,
          seriesName: YIELD_SERIES_NAMES,
          showForNullSeries: false,
          title: { text: 'Yield' },
          labels: { formatter: formatYield },
        },
      ]
    : {
        forceNiceScale: true,
        seriesName: PRICE_SERIES_NAMES,
        title: { text: 'Price' },
        labels: { formatter: formatPrice },
      },
});

const AfsEodPriceYieldChart: React.FC<Props> = ({ data, intraday = false }) => {
  const series = useMemo(() => buildAfsEodSeries(data), [data]);
  const hasYieldData = series.length > PRICE_SERIES_NAMES.length;
  const options = useMemo(
    () => ({
      ...buildAfsEodOptions(hasYieldData, intraday),
      ...(intraday && data.length === 1 ? { markers: { size: 4 } } : {}),
    }),
    [hasYieldData, intraday, data.length],
  );

  return (
    <>
      <Chart options={options} series={series} type="line" />
      {!hasYieldData && (
        <Typography.Text type="secondary">
          No stored {intraday ? 'intraday' : 'EOD'} yield observations are
          available for this bond.
        </Typography.Text>
      )}
    </>
  );
};

export default AfsEodPriceYieldChart;
